import React, { useMemo, useState } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import WifiIcon from "@mui/icons-material/Wifi";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import { useDeploymentStream } from "src/domains/application/hooks/useDeploymentStream";
import { useK8sEvents } from "src/domains/application/hooks/useK8sEvents";
import { EmptyState } from "src/shell/components/EmptyState";
import { ErrorMessage } from "src/shell/components/ErrorMessage";

import { formatRelativeTime, formatEventType } from "./shared/pipeline-helpers";

interface EventsTabProps {
  alias: string;
}

type EventSource = "k8s" | "pipeline";
type SeverityFilter = "all" | "warning" | "normal";

export function EventsTab({ alias }: EventsTabProps): React.ReactElement {
  const theme = useTheme();
  const { events, loading, error, connected, refresh } = useK8sEvents(alias);
  const {
    events: pipelineEvents,
    loading: pipelineLoading,
    error: pipelineError,
    connected: pipelineConnected,
    refetch: pipelineRefetch,
  } = useDeploymentStream(alias);

  const [source, setSource] = useState<EventSource>("pipeline");
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>("all");
  const [search, setSearch] = useState("");

  const isK8s = source === "k8s";

  const filteredK8sEvents = useMemo(() => {
    return events.filter((ev) => {
      if (severityFilter === "warning" && ev.type !== "Warning") return false;
      if (severityFilter === "normal" && ev.type === "Warning") return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          ev.involvedObjectName.toLowerCase().includes(q) ||
          ev.reason.toLowerCase().includes(q) ||
          ev.message.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [events, severityFilter, search]);

  const filteredPipelineEvents = useMemo(() => {
    const sorted = [...pipelineEvents].reverse();
    return sorted.filter((ev) => {
      if (search) {
        const q = search.toLowerCase();
        return (
          ev.eventType.toLowerCase().includes(q) ||
          ev.message.toLowerCase().includes(q) ||
          ev.step.toLowerCase().includes(q) ||
          (ev.metadata?.serviceName &&
            String(ev.metadata.serviceName).toLowerCase().includes(q))
        );
      }
      if (severityFilter === "warning" && ev.severity !== "error") return false;
      if (severityFilter === "normal" && ev.severity === "error") return false;
      return true;
    });
  }, [pipelineEvents, severityFilter, search]);

  const displayedEvents = isK8s ? filteredK8sEvents : filteredPipelineEvents;
  const isLoading = isK8s ? loading : pipelineLoading;
  const hasError = isK8s ? error : pipelineError;
  const isEmpty = isK8s ? events.length === 0 : pipelineEvents.length === 0;
  const isLive = isK8s ? connected : pipelineConnected;

  // Severity dot color
  function severityDotColor(severity: string): string {
    if (severity === "error") return "#ef4444";
    if (severity === "warning") return "#f59e0b";
    return "#10b981";
  }

  return (
    <Stack spacing={2}>
      {/* Source toggle + Live indicator */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            size="small"
            value={source}
            exclusive
            onChange={(_, v: EventSource | null) => {
              if (v) setSource(v);
            }}
            aria-label="Event source"
          >
            <ToggleButton
              value="pipeline"
              sx={{ px: 1.5, fontSize: 13, textTransform: "none" }}
            >
              Pipeline
            </ToggleButton>
            <ToggleButton
              value="k8s"
              sx={{ px: 1.5, fontSize: 13, textTransform: "none" }}
            >
              K8s
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip
            title={isLive ? "Live — real-time updates active" : "Connecting…"}
          >
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ cursor: "default" }}
            >
              {isLive ? (
                <WifiIcon fontSize="small" color="success" />
              ) : (
                <WifiOffIcon fontSize="small" color="disabled" />
              )}
              <Typography
                variant="caption"
                color={isLive ? "success.main" : "text.disabled"}
              >
                {isLive ? "Live" : "Connecting…"}
              </Typography>
            </Stack>
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            size="small"
            value={severityFilter}
            exclusive
            onChange={(_, v) => {
              if (v) setSeverityFilter(v as SeverityFilter);
            }}
            aria-label="Filter by severity"
          >
            <ToggleButton
              value="all"
              sx={{ px: 1.5, fontSize: 13, textTransform: "none" }}
            >
              All
            </ToggleButton>
            <ToggleButton
              value="warning"
              sx={{ px: 1.5, fontSize: 13, textTransform: "none" }}
            >
              <WarningAmberIcon sx={{ fontSize: 14, mr: 0.5 }} />
              {isK8s ? "Errors" : "Errors"}
            </ToggleButton>
            <ToggleButton
              value="normal"
              sx={{ px: 1.5, fontSize: 13, textTransform: "none" }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 14, mr: 0.5 }} />
              Normal
            </ToggleButton>
          </ToggleButtonGroup>
          {!isLive && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => (isK8s ? void refresh() : void pipelineRefetch())}
              disabled={isLoading}
              sx={{ textTransform: "none" }}
            >
              Refresh
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Search field */}
      <TextField
        size="small"
        placeholder={
          isK8s
            ? "Search by resource name, reason, or message…"
            : "Search by event type, message, step, or service…"
        }
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
      />

      {hasError && <ErrorMessage message={hasError} sx={{ mb: 0 }} />}

      {isLoading && isEmpty && (
        <Stack spacing={1}>
          {[1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={48}
              sx={{ borderRadius: 1.5 }}
            />
          ))}
        </Stack>
      )}

      {!isLoading && isEmpty && !hasError && (
        <EmptyState
          icon={ScheduleIcon}
          title={isK8s ? "No Kubernetes events yet" : "No pipeline events yet"}
          subtitle={
            isK8s
              ? "Events appear once the stack is deployed to a K8s cluster."
              : "Pipeline events appear when a build or deploy is triggered."
          }
        />
      )}

      {!isLoading && displayedEvents.length === 0 && !isEmpty && (
        <EmptyState
          icon={SearchIcon}
          title="No matching events"
          subtitle="Try adjusting your filter or search query."
        />
      )}

      {/* Event table */}
      {!isLoading && displayedEvents.length > 0 && (
        <TableContainer
          sx={{
            borderRadius: 1.5,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                  "& .MuiTableCell-head": {
                    fontSize: 12,
                    fontWeight: 600,
                    color: "text.secondary",
                    textTransform: "uppercase",
                  },
                }}
              >
                <TableCell sx={{ width: 100 }}>Time</TableCell>
                <TableCell sx={{ width: 160 }}>Type</TableCell>
                <TableCell sx={{ width: 100 }}>Severity</TableCell>
                <TableCell>Message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isK8s
                ? filteredK8sEvents.map((ev) => (
                    <TableRow
                      key={ev.name}
                      sx={{
                        "&:hover": { bgcolor: "action.hover" },
                        ...(ev.type === "Warning" && {
                          bgcolor: alpha(theme.palette.warning.main, 0.04),
                        }),
                      }}
                    >
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {ev.lastTimestamp
                            ? formatRelativeTime(ev.lastTimestamp)
                            : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: 11,
                            fontWeight: 500,
                          }}
                        >
                          {ev.reason}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor:
                                ev.type === "Warning" ? "#f59e0b" : "#10b981",
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {ev.type === "Warning" ? "Warning" : "Normal"}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: 13 }}>
                          {ev.involvedObjectKind}/{ev.involvedObjectName}:{" "}
                          {ev.message}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                : filteredPipelineEvents.map((ev) => (
                    <TableRow
                      key={ev.id}
                      sx={{
                        "&:hover": { bgcolor: "action.hover" },
                        ...(ev.severity === "error" && {
                          bgcolor: alpha(theme.palette.error.main, 0.04),
                        }),
                      }}
                    >
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatRelativeTime(ev.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: 11,
                            fontWeight: 500,
                          }}
                        >
                          {formatEventType(ev.eventType)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: severityDotColor(ev.severity),
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {ev.severity === "error"
                              ? "Error"
                              : ev.severity === "warning"
                                ? "Warning"
                                : "Normal"}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: 13 }}>
                          {String(ev.message)}
                          {typeof ev.metadata?.serviceName === "string" && (
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1, fontFamily: "monospace" }}
                            >
                              {"· " + ev.metadata.serviceName}
                            </Typography>
                          )}
                          {ev.severity === "error" &&
                            typeof ev.metadata?.error === "string" && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="error.main"
                                sx={{ display: "block", mt: 0.25 }}
                              >
                                {ev.metadata.error}
                              </Typography>
                            )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}

export default EventsTab;
